<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Conceptos de pago operativo</h1>
    </div>

    <wz-table-container>
      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>
        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-button variant="primary" @click="$router.push({ name: 'operational-payment-concept-create' })">Agregar</wz-button>
        </template>
        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'code' }" @click="toggleSort('code')">
              Código <i :class="['wz-th-sort-icon', sortIcon('code')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'name' }" @click="toggleSort('name')">
              Nombre <i :class="['wz-th-sort-icon', sortIcon('name')]"></i>
            </th>
            <th>Descripción</th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="sortedList(list).length === 0">
            <td colspan="5" class="text-center text-muted py-4">No se encontraron resultados.</td>
          </tr>
          <tr v-for="o in sortedList(list)" :key="o.id">
            <td>{{ o.code }}</td>
            <td>{{ o.name }}</td>
            <td>{{ o.description || '—' }}</td>
            <td>
              <wz-badge :variant="getStatus(o.status).color" pill>{{ getStatus(o.status).label }}</wz-badge>
            </td>
            <td style="text-align: center">
              <wz-dropdown
                :items="[
                  { key: 'edit',   label: 'Editar' },
                  { key: 'status', label: getStatus(o.status).action },
                ]"
                placement="left"
                @click="handleAction(o, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ list.length }} registro(s)</span>
      </wz-table-footer>
    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { FilterMixin } from '@wizepe/wizeui'
import { SortMixin, ConfirmModalMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants } from '@/core/utils'
import { OperationalPaymentConceptService } from '@/core/services'

const STATUS = {
  ACTIVE:   { key: 'ACTIVE',   label: 'Activo',   color: 'success-solid',   action: 'Desactivar' },
  INACTIVE: { key: 'INACTIVE', label: 'Inactivo', color: 'secondary-solid', action: 'Activar' },
}

export default {
  mixins: [SortMixin, FilterMixin, ConfirmModalMixin],
  data() {
    return {
      list: [],
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: LocalConstants.TypeFilters.SINGLE,
          options: [
            { key: STATUS.ACTIVE.key, label: STATUS.ACTIVE.label },
            { key: STATUS.INACTIVE.key, label: STATUS.INACTIVE.label },
          ],
        },
      ],
    }
  },
  methods: {
    getStatus(status) {
      return Number(status) === 1 ? STATUS.ACTIVE : STATUS.INACTIVE
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await OperationalPaymentConceptService.list()
        let items = resp.payload || []
        const s = this.filters.status
        if (s === STATUS.ACTIVE.key) items = items.filter(o => Number(o.status) === 1)
        else if (s === STATUS.INACTIVE.key) items = items.filter(o => Number(o.status) === 0)
        this.list = items
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar conceptos de pago operativo')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    handleAction(o, item) {
      if (item.key === 'edit')   this.$router.push({ name: 'operational-payment-concept-edit', params: { id: o.id } })
      if (item.key === 'status') this.openChangeStatus(o)
    },
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmModal({
        title:       'Cambiar estado',
        description: '¿Está seguro que desea cambiar el estado?',
        acceptText:  'Aceptar',
        action:      'update',
      })
    },
    onModalConfirm() {
      const action = this.confirmModal.action
      this.closeConfirmModal()
      if (action === 'update') this.changeStatus()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        const status = Number(this.selectedOption.status) === 1 ? 0 : 1
        await OperationalPaymentConceptService.changeStatus(this.selectedOption.id, status)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cambiar estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() { await this.loadData() },
}
</script>

<style lang="stylus"></style>
